import {ControlledEZPRSelect} from '../utils/ControlledEZPRSelect.jsx';
import React, {useState, useMemo, useEffect} from 'react';
import PropTypes from 'prop-types';
import {TextField, Chip, Dialog, DialogContent, DialogContentText, DialogTitle} from '@mui/material';
import {ezprOrange, wayOfBuyingOptions} from '../../_helpers';
import {requestStores} from '../../ListOfPREditors.brick.jsx';
import {checkLabelValueObject} from '../../../utils/checkObjects';
import {UserField} from './UserField.jsx';
import {PurchaseRequest} from '../../../models/PurchaseRequest';
import {validateEmail} from '../../../utils/email';
import {getEmailByUserIdGraphAPILambda, getUserDefaultsLambda} from '../../../utils/functionSignatures';
import {ErrorPopup} from '../dialogs/ErrorPopup.jsx';
import {LAMBDA_NAMES, mandatoryFieldsForRequester, mandatoryFieldsForSpender} from '../../../utils/constants';
import {BrickContext} from 'olympe';
import {useFieldEditability} from '../utils/useFieldEditability.js';

const PROJECT_TITLE_TEXTFIELD_STYLE = {width: '50%'};
const USER_FIELD_SX = {width: '250px'};

/**
 * @param {GeneralDetails.propTypes} props
 * @return {JSX.Element}
 * @constructor
 */
export function GeneralDetails(props) {
    const [selectedDL, setSelectedDL] = useState(null);
    const [loadingSpender, setLoadingSpender] = useState(false);
    const [loadingRequester, setLoadingRequester] = useState(false);
    const [errorPopupOpen, setErrorPopupOpen] = useState(false);

    const useRequestStore = requestStores.get(props.prTag);
    const [projectTitle, setProjectTitle] = useRequestStore((state) => [state.projectTitle, state.setProjectTitle]);
    const [wayOfBuying, setWayOfBuying] = useRequestStore((state) => [state.wayOfBuying, state.setWayOfBuying]);
    const [spender, setSpender] = useRequestStore((state) => [state.spender, state.setSpender]);
    const [requester, setRequester] = useRequestStore((state) => [state.requester, state.setRequester]);
    const operationalBuyersInDL = useRequestStore((state) => state.operationalBuyersInDL);
    const strategicBuyers = useRequestStore((state) => state.strategicBuyers);
    const sbApprover = useRequestStore((state) => state.sbApprover);
    const obApprover = useRequestStore((state) => state.obApprover);
    const getStatus = useRequestStore((state) => state.getStatus);
    const denialInfo = useRequestStore((state) => state.denialInfo);
    const sbDenialInfo = useRequestStore((state) => state.sbDenialInfo);
    const userIdToCallSAPAPIs = useRequestStore((state) => state.userIdToCallSAPAPIs);
    const prLocked = useRequestStore((state) => state.prLocked);
    const [highlightMandatoryFieldForRequester, highlightMandatoryFieldForSpender] = useRequestStore((state) => [state.highlightMandatoryFieldForRequester, state.highlightMandatoryFieldForSpender]);
    let buyerType = 'NA';

    const userDefaultsLambda = props.context.get(LAMBDA_NAMES.GET_USER_DEFAULT_INFO);
    const [userDefaults, setUserDefaults] = useState(null);

    const {isDisabled} = useFieldEditability(props.prTag, {context: props.context});

    const getUserIDGraphAPI = props.context.get(LAMBDA_NAMES.GET_USER_ID_GRAPH_API);

    useEffect(()=>{
        let cancelled = false;

        getUserDefaultsLambda(props.context, userDefaultsLambda,
            (results)=> {
                if (!cancelled) setUserDefaults(results);
            },
            ()=>{
                console.log('Error retrieving user defaults');
            });

        return () => {
            cancelled = true;
        };
    }, []);

    // This useEffect will check if the spender and requester contains an userID. If not (if the value is an email), the
    // system will use the user ID API to find the corresponding full name and user id.
    useEffect(() => {
        const isSpenderAnEmail = validateEmail(spender.value);
        const isRequesterAnEmail = validateEmail(requester.value);
        if (isSpenderAnEmail || isRequesterAnEmail) {
            let cancelled = false;

            // We create this map here to be able to call only one time the API, even if there are two user IDs to fetch
            // (spender and requester). We need it because we need to know who corresponds to each email.
            const map = new Map();

            if (isSpenderAnEmail) {
                map.set('spender', spender.email);
                setLoadingSpender(true);
            }
            if (isRequesterAnEmail) {
                map.set('requester', requester.email);
                setLoadingRequester(true);
            }

            getEmailByUserIdGraphAPILambda(props.context, getUserIDGraphAPI, Array.from(map.values()))
                .then((result) => {
                    if (cancelled) return;

                    try {
                        const parsedResult = JSON.parse(result).responses.flatMap((row) => row.body.value).map((row) => ({
                            label: `${row['givenName']} ${row['surname']}`,
                            value: row['onPremisesSamAccountName'],
                            email: row['userPrincipalName'],
                        }));

                        if (map.has('spender')) {
                            const spenderFound = parsedResult.find((r) => r.email === spender.email);
                            if (spenderFound) { // If the spender is not found by the API, we leave the data as is it
                                setSpender({
                                    label: spenderFound.label,
                                    value: spenderFound.value,
                                    email: spenderFound.email,
                                });
                            }
                        }

                        if (map.has('requester')) {
                            const requesterFound = parsedResult.find((r) => r.email === requester.email);
                            if (requesterFound) {
                                setRequester({
                                    label: requesterFound.label,
                                    value: requesterFound.value,
                                    email: requesterFound.email,
                                });
                            }
                        }
                    } catch (e) {
                        console.error(e);
                    }
                    setLoadingSpender(false);
                    setLoadingRequester(false);
                })
                .catch(() => {
                    if (!cancelled) {
                        setLoadingSpender(false);
                        setLoadingRequester(false);
                    }
                });

            return () => {
                cancelled = true;
            };
        }
    }, []);

    switch (getStatus()) {
    case PurchaseRequest.statuses.pendingNbs:
    case PurchaseRequest.statuses.deniedByOB:
        buyerType = 'OB';
        break;
    case PurchaseRequest.statuses.pendingSB:
    case PurchaseRequest.statuses.deniedBySB:
        buyerType = 'SB';
        break;
    default:
        buyerType = 'NA';
    }

    const handleWayOfBuyingChange = (_, newValue) => {
        setWayOfBuying(checkLabelValueObject(newValue), userDefaults);
    };

    const handleProjectTitleChange = (event) => {
        setProjectTitle(event.target.value);
    };

    const handleSpenderChange = (newValue) => {
        setSpender(newValue);
    };

    const handleRequesterChange = (newValue) => {
        setRequester(newValue);
    };

    const handleCloseErrorPopup = () => {
        setErrorPopupOpen(false);
    };

    const handleClickChip = (dl) => {
        setSelectedDL(dl);
    };

    const buyerField = () => {
        switch (getStatus()) {
        case PurchaseRequest.statuses.pendingNbs: return operationalBuyersInDL?.map((dl, index) => <Chip className={'pr-editor-general-details-buyers-chip'} clickable={!!dl.ob.length} key={index} label={dl?.dl} onClick={() => !!dl.ob.length ? handleClickChip(dl) : null} />);
        case PurchaseRequest.statuses.deniedByOB: return <TextField variant={'standard'} defaultValue={denialInfo?.buyer?.label} disabled />;
        case PurchaseRequest.statuses.pendingSB: return strategicBuyers?.map((sb, index) => <Chip className={'pr-editor-general-details-buyers-chip'} key={index} label={sb.label}/>);
        case PurchaseRequest.statuses.deniedBySB: return <TextField variant={'standard'} defaultValue={sbDenialInfo?.buyer?.label} disabled />;
        default: return null;
        }
    };

    const currentWayOfBuyingOptions = useMemo(() => {
        return wayOfBuyingOptions.filter(({value}) => wayOfBuying.availableOptions.includes(value));
    }, [wayOfBuying.availableOptions]);

    return (
        <div className={'pr-editor-centered-submodule'}>
            {prLocked &&
                <div className={'pr-editor-locked-message'}>
                    {`This PR is locked by ${props.usersCheckingThePr[0]?.userFullName}. You cannot modify it.`}
                </div>
            }
            <p className={'pr-editor-section-title'}>General details</p>
            <div className={'pr-editor-general-details-project-title'}>
                <div className={'pr-editor-general-details-project-title-label'}>Project/Order title:</div>
                <TextField
                    data-cy={'project_title'}
                    variant={'standard'}
                    style={PROJECT_TITLE_TEXTFIELD_STYLE}
                    defaultValue={projectTitle}
                    onBlur={handleProjectTitleChange}
                    disabled={isDisabled('generalDetails.projectTitle')}
                />
            </div>
            <div className={'pr-editor-general-details-next-lines'}>
                <div className={'pr-editor-general-details-next-lines-first-title'}>
                    <div>Way of buying: *</div>
                </div>
                <div className={'pr-editor-general-details-project-title-textField'}>
                    <div className={'pr-editor-general-details-container'}>
                        <ControlledEZPRSelect
                            options={currentWayOfBuyingOptions}
                            value={wayOfBuying.currentValue.label}
                            handleChange={handleWayOfBuyingChange}
                            minWidth={'250px'}
                            readOnly={isDisabled('generalDetails.wayOfBuying')}
                            placeholder='Please choose a Way of Buying'
                            highlightMandatoryFieldForRequester={highlightMandatoryFieldForRequester}
                            highlightMandatoryFieldForSpender={highlightMandatoryFieldForSpender}
                            mandatoryForSpender={mandatoryFieldsForSpender.includes('Way of Buying')}
                            mandatoryForRequester={mandatoryFieldsForRequester.includes('Way of Buying')}
                            freeSolo
                            preventTyping
                            valueFromSelectionOnly
                        />
                    </div>
                    <div className={'pr-editor-general-details-container'}>
                        <div>Spender: *</div>
                        <UserField
                            context={props.context}
                            wholeValue={spender}
                            initialValue={spender.label}
                            onChange={handleSpenderChange}
                            loading={loadingSpender}
                            sx={USER_FIELD_SX}
                            disabled={isDisabled('generalDetails.spender')}
                            highlightMandatoryFieldForRequester={highlightMandatoryFieldForRequester}
                            highlightMandatoryFieldForSpender={highlightMandatoryFieldForSpender}
                            mandatoryForSpender={mandatoryFieldsForSpender.includes('Spender')}
                            mandatoryForRequester={mandatoryFieldsForRequester.includes('Spender')}
                            userRole={'Spender'}
                            userIdToCallSAPAPIs={userIdToCallSAPAPIs}
                        />
                    </div>
                    <div className={'pr-editor-general-details-container'}>
                        <div>Requester: *</div>
                        <UserField
                            context={props.context}
                            wholeValue={requester}
                            initialValue={requester.label}
                            onChange={handleRequesterChange}
                            loading={loadingRequester}
                            sx={USER_FIELD_SX}
                            disabled={isDisabled('generalDetails.requester')}
                            highlightMandatoryFieldForRequester={highlightMandatoryFieldForRequester}
                            highlightMandatoryFieldForSpender={highlightMandatoryFieldForSpender}
                            mandatoryForSpender={mandatoryFieldsForSpender.includes('Requester')}
                            mandatoryForRequester={mandatoryFieldsForRequester.includes('Requester')}
                            userRole={'Requester'}
                            userIdToCallSAPAPIs={userIdToCallSAPAPIs}
                        />
                    </div>
                </div>
            </div>
            <div className={'pr-editor-general-details-next-lines'}>
                {obApprover ? (
                    <>
                        <div
                            className={'pr-editor-general-details-next-lines-first-title'}>Operational Buyer:</div>
                        <div className={'pr-editor-general-details-container'}>
                            <TextField
                                variant={'standard'}
                                disabled
                                value={obApprover}
                            />
                        </div>
                    </>
                ) : null}
                {sbApprover ? (
                    <>
                        <div className={'pr-editor-general-details-next-lines-first-title'} style={{marginLeft: obApprover && sbApprover ? '20.5rem' : '0'}}>Strategic Buyer:</div>
                        <div className={'pr-editor-general-details-container'}>
                            <TextField
                                variant={'standard'}
                                disabled
                                value={sbApprover}
                            />
                        </div>
                    </>
                ) : null}
            </div>
            <div className={'pr-editor-general-details-next-lines'}>
                {buyerType !== 'NA' && (
                    <>
                        <div
                            className={'pr-editor-general-details-next-lines-first-title'}>{buyerType === 'OB' ? 'Operational Buyer:' : 'Strategic Buyer:'}</div>
                        <div className='distribution-list-container'>
                            {buyerField()}
                        </div>
                    </>
                )}
                {selectedDL && (
                    <Dialog
                        onClose={() => setSelectedDL(null)}
                        open={!!selectedDL}
                        aria-labelledby="selected-dl-popup"
                    >
                        <div style={{display: 'flex'}}>
                            <div>
                                <DialogTitle id="selected-dl-title">Operational buyers
                                    for {selectedDL?.dl}</DialogTitle>
                                <DialogContent>
                                    {selectedDL?.ob?.map((ob, index) => (
                                        <DialogContentText key={index}>{ob.label}: {ob.email}</DialogContentText>
                                    ))}
                                </DialogContent>
                            </div>
                        </div>
                    </Dialog>
                )}
            </div>
            <ErrorPopup
                onClose={handleCloseErrorPopup}
                open={errorPopupOpen}
                title={'Unauthorized user'}
                errorMessages={[`The requester you selected doesn't have the sufficient authorization to submit a PR.`]}
                color={ezprOrange}
                icon={'front_hand'}
            />
        </div>
    );
}

GeneralDetails.propTypes = {
    refs: PropTypes.object,
    prTag: PropTypes.string,
    context: PropTypes.instanceOf(BrickContext).isRequired,
    usersCheckingThePr: PropTypes.array,
};
